// theme-system/FooterExclusions.js
// Page rules for hiding footer content and/or bottomNav — driven by schema footer config.

import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { WebUrl } from '../util/Constant';
import { stripLocaleFromPath } from '../util/Util';
import { useSchema } from './SchemaContext';

/** Class on `.layout__main` when footer content is hidden. */
export const LAYOUT_MAIN_NO_FOOTER_CLASS = 'layout__main--no-footer';

/** Matches common theme mobile breakpoint (bottom nav / lg). */
const DESKTOP_MIN_WIDTH = 900;

function getFooterConfigFromSchema(schema) {
  return schema?.themeSettings?.footer?.components?.find(
    (c) => c.type === 'Footer'
  );
}

function isPageExcluded(pageKey, isDesktop, schemaPages) {
  if (!pageKey || !schemaPages) return false;
  const list = isDesktop ? schemaPages.desktop : schemaPages.mobile;
  return Array.isArray(list) && list.includes(pageKey);
}

export function shouldHideFooterContent(pageKey, isDesktop, schema) {
  const footerConfig = getFooterConfigFromSchema(schema);
  return isPageExcluded(pageKey, isDesktop, footerConfig?.pagesWithoutFooter);
}

export function shouldHideBottomNav(pageKey, isDesktop, schema) {
  const footerConfig = getFooterConfigFromSchema(schema);
  return isPageExcluded(pageKey, isDesktop, footerConfig?.pagesWithoutBottomNav);
}

/**
 * Derive page key from pathname: "/en/livechat" -> "livechat"
 * @param {string} pathname
 * @returns {string}
 */
export function getPageKeyFromPathname(pathname) {
  const stripped = stripLocaleFromPath(
    pathname || '',
    WebUrl.getCurrentLocale()
  );
  const segment = stripped.replace(/^\//, '').split('/')[0];
  return segment || '';
}

function useExclusionContext() {
  const location = useLocation();
  const schema = useSchema();
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== 'undefined' && window.innerWidth >= DESKTOP_MIN_WIDTH
  );

  useEffect(() => {
    const onResize = () => {
      setIsDesktop(window.innerWidth >= DESKTOP_MIN_WIDTH);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  return {
    pageKey: getPageKeyFromPathname(location.pathname),
    isDesktop,
    schema,
  };
}

/** Hide footer content on excluded pages (from schema). */
export function useHideFooterContent() {
  const { pageKey, isDesktop, schema } = useExclusionContext();
  return shouldHideFooterContent(pageKey, isDesktop, schema);
}

/** Hide bottomNav on excluded pages (from schema). */
export function useHideBottomNav() {
  const { pageKey, isDesktop, schema } = useExclusionContext();
  return shouldHideBottomNav(pageKey, isDesktop, schema);
}
